import { Outlet, useOutletContext } from 'react-router-dom'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { filterEmployeesByPayrollPolicyPeriod } from 'modules/payroll_bundle/lib/filterEmployeesByPayrollPolicyPeriod'
import { usePolicyPage } from 'modules/payroll_bundle/lib/policyPageContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'
import type Policy from 'modules/payroll_bundle/store/models/Policy'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

export interface PayrollBundlePageContext {
  employees: Employee[]
  filteredEmployees: Employee[]
  policyPeriod?: PolicyPeriod
  selectedPolicy: Policy
  returnTo?: string
  navigateTo?: string
}

interface Props {
  status?: PayrollStatus
}

const PayrollBundleRootPage = ({ status }: Props) => {
  const { policyId, currentPeriod: policyPeriod } = usePolicyPeriods()
  const { loading: loadingPolicyAuthorizations } = useAsyncData(async () => {
    await Promise.all([
      policyAuthorizations.authorizePolicies(
        'payroll_supplements.supplements.read',
        employees.models
      ),
      policyAuthorizations.authorizePolicies(
        'payroll_supplements.observations.read',
        employees.models
      ),
    ])
  }, [])

  const visibleEmployees = employees.filter(
    (employee) =>
      policyAuthorizations.for('payroll_supplements.supplements.read', employee) ||
      policyAuthorizations.for('payroll_supplements.observations.read', employee)
  )
  const policies = usePolicies()
  const { policyCurrentContracts: currentContracts, loading: loadingContracts } = usePolicyPage()
  const selectedPeriodEmployees = useUnsafeMemo(
    () =>
      !policyPeriod || loadingContracts
        ? []
        : filterEmployeesByPayrollPolicyPeriod({
            employees: visibleEmployees,
            policyPeriod,
            currentContracts,
            policies,
          }),
    [visibleEmployees, policyPeriod, policies, loadingContracts] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const selectedPolicy = policyId ? policies.get(policyId) : null

  if (!selectedPolicy || !policyId) return null

  const pageContext = {
    employees: visibleEmployees,
    filteredEmployees: selectedPeriodEmployees,
    policyPeriod,
    selectedPolicy,
  } satisfies PayrollBundlePageContext

  return <Outlet context={pageContext} />
}

export default observer(PayrollBundleRootPage)

export const usePayrollBundlePageContext = () => {
  return useOutletContext<PayrollBundlePageContext>()
}
